import { useState, useEffect } from 'react';
import axios from 'axios';
import { PackageCheck, CheckCircle, X, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { PaginationControls } from '@/components/ui/pagination-controls';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GrnView = () => {
  const [grns, setGrns] = useState([]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [pos, setPos] = useState([]);
  const [showQADialog, setShowQADialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedGrn, setSelectedGrn] = useState(null);
  const [qaAction, setQaAction] = useState(''); // 'approve' or 'reject'
  const [qaRemarks, setQaRemarks] = useState('');
  const [editQuantities, setEditQuantities] = useState({
    good_qty: 0,
    rejected_qty: 0,
    rejection_type: 'REJECTED'  // REWORK or REJECTED
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0
  });
  const [sortBy, setSortBy] = useState('received_date');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    fetchGrns();
  }, [pagination.page, sortBy, sortOrder]);

  const fetchMasterData = async () => {
    try {
      const [itemRes, vendorRes, whRes, poRes] = await Promise.all([
        axios.get(`${API}/items`),
        axios.get(`${API}/vendors`),
        axios.get(`${API}/warehouses`),
        axios.get(`${API}/procurement/pos`)
      ]);
      setItems(itemRes.data);
      setVendors(vendorRes.data);
      setWarehouses(whRes.data);
      const poData = Array.isArray(poRes.data) ? poRes.data : (poRes.data.data || []);
      setPos(poData);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchGrns = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      const response = await axios.get(`${API}/grn?${params.toString()}`);
      
      if (response.data.data) {
        setGrns(response.data.data);
        setPagination(prev => ({
          ...prev,
          totalCount: response.data.pagination.total_count,
          totalPages: response.data.pagination.total_pages
        }));
      } else {
        setGrns(response.data);
      }
    } catch (error) {
      console.error('Error fetching GRNs:', error);
      toast.error('Failed to load GRN data');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getItemInfo = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : id;
  };

  const getVendorName = (id) => vendors.find(v => v.id === id)?.name || 'N/A';
  const getWarehouseName = (id) => warehouses.find(w => w.id === id)?.name || 'N/A';
  const getPoNumber = (id) => pos.find(p => p.id === id)?.po_number || 'N/A';

  const openQADialog = (grn, action) => {
    setSelectedGrn(grn);
    setQaAction(action);
    setQaRemarks('');
    setShowQADialog(true);
  };

  const openEditDialog = (grn) => {
    setSelectedGrn(grn);
    setEditQuantities({
      good_qty: grn.good_qty !== null && grn.good_qty !== undefined ? grn.good_qty : grn.qty,
      rejected_qty: grn.rejected_qty !== null && grn.rejected_qty !== undefined ? grn.rejected_qty : 0
    });
    setShowEditDialog(true);
  };

  const handleEditQuantities = async () => {
    if (!selectedGrn) return;

    const { good_qty, rejected_qty } = editQuantities;
    const total = good_qty + rejected_qty;

    if (Math.abs(total - selectedGrn.qty) > 0.01) {
      toast.error(`Good qty + Rejected qty must equal total received qty (${selectedGrn.qty})`);
      return;
    }

    if (good_qty < 0 || rejected_qty < 0) {
      toast.error('Quantities cannot be negative');
      return;
    }

    try {
      await axios.put(`${API}/grn/${selectedGrn.id}/quantities`, null, {
        params: {
          good_qty: good_qty,
          rejected_qty: rejected_qty,
          rejection_type: editQuantities.rejection_type
        }
      });
      toast.success('Quantities updated successfully');
      setShowEditDialog(false);
      fetchGrns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update quantities');
    }
  };

  const handleQAAction = async () => {
    if (!selectedGrn) return;

    if (!qaRemarks.trim()) {
      toast.error('Remarks are required');
      return;
    }

    try {
      const endpoint = qaAction === 'approve' 
        ? `${API}/grn/${selectedGrn.id}/qa-approve`
        : `${API}/grn/${selectedGrn.id}/qa-reject`;
      
      const response = await axios.post(endpoint, null, {
        params: qaAction === 'approve' && qaRemarks ? { remarks: qaRemarks } : { remarks: qaRemarks }
      });
      
      toast.success(response.data.message);
      setShowQADialog(false);
      fetchGrns();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${qaAction} GRN`);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending QA</Badge>;
      case 'APPROVED':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const pendingGrns = grns.filter(g => g.qa_status === 'PENDING');
  const processedGrns = grns.filter(g => g.qa_status !== 'PENDING');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Goods Receipt Note (GRN)</h2>
          <p className="text-gray-500">GRNs are automatically created from shipments. Manage QA inspection here.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Pending QA</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingGrns.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Approved</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {grns.filter(g => g.qa_status === 'APPROVED').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Rejected</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {grns.filter(g => g.qa_status === 'REJECTED').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending QA GRNs */}
      {pendingGrns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              Pending QA Inspection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative w-full overflow-auto">
              <table className="w-full caption-bottom text-sm">
                <thead className="[&_tr]:border-b">
                  <tr className="border-b transition-colors hover:bg-muted/50">
                    <th className="h-10 px-2 text-left align-middle font-medium">GRN Number</th>
                    <th className="h-10 px-2 text-left align-middle font-medium">Date</th>
                    <th className="h-10 px-2 text-left align-middle font-medium">Vendor</th>
                    <th className="h-10 px-2 text-left align-middle font-medium">Item</th>
                    <th className="h-10 px-2 text-left align-middle font-medium">Total Qty</th>
                    <th className="h-10 px-2 text-left align-middle font-medium">Good Qty</th>
                    <th className="h-10 px-2 text-left align-middle font-medium">Rejected Qty</th>
                    <th className="h-10 px-2 text-left align-middle font-medium">Warehouse</th>
                    <th className="h-10 px-2 text-left align-middle font-medium">MRO Number</th>
                    <th className="h-10 px-2 text-left align-middle font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="[&_tr:last-child]:border-0">
                  {pendingGrns.map((grn) => (
                    <tr key={grn.id} className="border-b transition-colors hover:bg-muted/50">
                      <td className="p-2 align-middle font-medium">{grn.grn_number}</td>
                      <td className="p-2 align-middle">{grn.received_date}</td>
                      <td className="p-2 align-middle">{getVendorName(grn.vendor_id)}</td>
                      <td className="p-2 align-middle">{getItemInfo(grn.item_id)}</td>
                      <td className="p-2 align-middle font-semibold">{grn.qty}</td>
                      <td className="p-2 align-middle">
                        <span className="text-green-600 font-medium">
                          {grn.good_qty !== null && grn.good_qty !== undefined ? grn.good_qty : '-'}
                        </span>
                      </td>
                      <td className="p-2 align-middle">
                        <span className="text-red-600 font-medium">
                          {grn.rejected_qty !== null && grn.rejected_qty !== undefined ? grn.rejected_qty : '-'}
                        </span>
                      </td>
                      <td className="p-2 align-middle">{getWarehouseName(grn.warehouse_id)}</td>
                      <td className="p-2 align-middle">{grn.po_id ? getPoNumber(grn.po_id) : '-'}</td>
                      <td className="p-2 align-middle">
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 hover:bg-blue-50"
                            onClick={() => openEditDialog(grn)}
                          >
                            Edit Qty
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:bg-green-50"
                            onClick={() => openQADialog(grn, 'approve')}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => openQADialog(grn, 'reject')}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All GRN Records */}
      <Card>
        <CardHeader>
          <CardTitle>All GRN Records</CardTitle>
          <CardDescription>Complete history of goods receipts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative w-full overflow-auto">
            <table className="w-full caption-bottom text-sm">
              <thead className="[&_tr]:border-b">
                <tr className="border-b transition-colors hover:bg-muted/50">
                  <th className="h-10 px-2 text-left align-middle font-medium">GRN Number</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Date</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Vendor</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Item</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Total Qty</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Good Qty</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Rejected Qty</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Warehouse</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Status</th>
                  <th className="h-10 px-2 text-left align-middle font-medium">Remarks</th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {grns.map((grn) => (
                  <tr key={grn.id} className="border-b transition-colors hover:bg-muted/50">
                    <td className="p-2 align-middle font-medium">{grn.grn_number}</td>
                    <td className="p-2 align-middle">{grn.received_date}</td>
                    <td className="p-2 align-middle">{getVendorName(grn.vendor_id)}</td>
                    <td className="p-2 align-middle">{getItemInfo(grn.item_id)}</td>
                    <td className="p-2 align-middle font-semibold">{grn.qty}</td>
                    <td className="p-2 align-middle">
                      <span className="text-green-600 font-medium">
                        {grn.good_qty !== null && grn.good_qty !== undefined ? grn.good_qty : '-'}
                      </span>
                    </td>
                    <td className="p-2 align-middle">
                      <span className="text-red-600 font-medium">
                        {grn.rejected_qty !== null && grn.rejected_qty !== undefined ? grn.rejected_qty : '-'}
                      </span>
                    </td>
                    <td className="p-2 align-middle">{getWarehouseName(grn.warehouse_id)}</td>
                    <td className="p-2 align-middle">{getStatusBadge(grn.qa_status)}</td>
                    <td className="p-2 align-middle text-sm text-gray-600">{grn.qa_remarks || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          <PaginationControls pagination={pagination} onPageChange={handlePageChange} />
        </CardContent>
      </Card>

      {/* Edit Quantities Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit GRN Quantities - QA Inspection</DialogTitle>
          </DialogHeader>
          {selectedGrn && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded-md">
                <p className="text-sm text-slate-600">GRN: <span className="font-semibold">{selectedGrn.grn_number}</span></p>
                <p className="text-sm text-slate-600">Total Received: <span className="font-semibold">{selectedGrn.qty}</span></p>
              </div>
              
              <div>
                <Label htmlFor="good_qty">Good Quantity (to be added to inventory) *</Label>
                <Input
                  id="good_qty"
                  type="number"
                  value={editQuantities.good_qty}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setEditQuantities({
                      ...editQuantities,
                      good_qty: val,
                      rejected_qty: selectedGrn.qty - val
                    });
                  }}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="rejected_qty">Rejected Quantity (will NOT be added to inventory) *</Label>
                <Input
                  id="rejected_qty"
                  type="number"
                  value={editQuantities.rejected_qty}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setEditQuantities({
                      ...editQuantities,
                      rejected_qty: val,
                      good_qty: selectedGrn.qty - val
                    });
                  }}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
              
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-md">
                <p className="text-sm font-medium text-blue-900">Validation:</p>
                <p className="text-sm text-blue-700">
                  Good Qty ({editQuantities.good_qty}) + Rejected Qty ({editQuantities.rejected_qty}) = 
                  <span className={`font-semibold ml-1 ${Math.abs((editQuantities.good_qty + editQuantities.rejected_qty) - selectedGrn.qty) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                    {editQuantities.good_qty + editQuantities.rejected_qty}
                  </span>
                  <span className="ml-1">(Must equal {selectedGrn.qty})</span>
                </p>
              </div>
              
              {editQuantities.rejected_qty > 0 && (
                <div className="bg-blue-50 border-blue-200 border p-3 rounded-md flex gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Auto-Parking: PENDING INSPECTION
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      Rejected quantity ({editQuantities.rejected_qty}) will be automatically parked as PENDING INSPECTION. 
                      Go to Inventory → Parked Inventory to segregate into REWORK (can fix) or REJECTED (return to vendor).
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditQuantities}>Save Quantities</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QA Action Dialog */}
      <Dialog open={showQADialog} onOpenChange={setShowQADialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {qaAction === 'approve' ? 'Approve' : 'Reject'} QA Inspection
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="qa_remarks">
              Remarks <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="qa_remarks"
              value={qaRemarks}
              onChange={(e) => setQaRemarks(e.target.value)}
              placeholder="Enter QA inspection remarks..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQADialog(false)}>Cancel</Button>
            <Button
              className={qaAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              onClick={handleQAAction}
            >
              {qaAction === 'approve' ? 'Approve & Add to Inventory' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GrnView;
